import React, { useMemo, useCallback, useRef, useState, useEffect, memo, MutableRefObject } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import { Folder, File as FileIcon, ArrowUp, ArrowDown } from 'lucide-react';
import { FileStat, FolderSizeResult } from '../../api/IFileSystem';
import { useFileIcon } from '../../hooks/useFileIcon';
import { formatDate, formatSize, processDropItems } from '../../utils/fileUtils';
import { PercentBar } from '../UI/PercentBar';
import { t } from '../../i18n';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useFileSearchStatusStore } from '../../store/useFileSearchStatus';
import { FileSearchStatus } from '../../../../types';
import { CSS } from '@dnd-kit/utilities';

// Constants
const ROW_HEIGHT = 40;
const VIRTUAL_THRESHOLD = 50; // Lowered from 200 to reduce render overhead

// Rename input component - auto-selects text only on mount
interface RenameInputProps {
  defaultValue: string;
  className: string;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const RenameInput: React.FC<RenameInputProps> = ({ defaultValue, className, onKeyDown, onBlur }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []); // Only run on mount

  return (
    <input
      ref={inputRef}
      defaultValue={defaultValue}
      className={className}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    />
  );
};

interface FileDetailsViewProps {
  onContextMenu: (e: React.MouseEvent, path: string) => void;
  folderSizes: Record<string, FolderSizeResult>;
  currentFolderSize: FolderSizeResult | null;
  fetchFolderSizes: (folders: FileStat[]) => void;
  activeDragFile?: FileStat | null;
  onDropFiles?: (files: { file: File, relativePath?: string }[], targetPath: string) => void;
}

const getFileExt = (name: string) => {
  const parts = name.split('.');
  if (parts.length <= 1) return '';
  return parts[parts.length - 1].toLowerCase();
};

const getTypeLabel = (file: FileStat) => {
  if (file.type === 'dir') return t('folder');
  if (file.type === 'drive') return t('drive');
  const ext = getFileExt(file.name);
  return ext || t('file');
};

// Memoized row component for virtual list
const VirtualRow = memo(({
  index,
  style,
  data
}: ListChildComponentProps<{
  files: FileStat[];
  selectedPaths: string[];
  renamingPath: string | null;
  folderSizes: Record<string, FolderSizeResult>;
  parentFolderSize: FolderSizeResult | null;
  activeDragFile: FileStat | null;
  isSearchEngineReady: boolean;
  searchEngineStatus: FileSearchStatus;
  openSearchEngineInitModal: (isReinit?: boolean) => void;
  onRowClick: (path: string, e: React.MouseEvent) => void;
  onRowDoubleClick: (file: FileStat) => void;
  onRowContextMenu: (e: React.MouseEvent, path: string) => void;
  onDropFiles?: (files: { file: File, relativePath?: string }[], targetPath: string) => void;
  onRenameSubmit: (oldPath: string, e: React.KeyboardEvent<HTMLInputElement>) => void;
  onRenameBlur: (oldPath: string, value: string) => void;
}>) => {
  const { files, selectedPaths, renamingPath, folderSizes, parentFolderSize, activeDragFile, isSearchEngineReady, searchEngineStatus, openSearchEngineInitModal, onRowClick, onRowDoubleClick, onRowContextMenu, onDropFiles, onRenameSubmit, onRenameBlur } = data;
  const file = files[index];
  const selected = selectedPaths.includes(file.path);
  const renaming = renamingPath === file.path;
  const folderSize = file.type === 'dir' ? folderSizes[file.path] : undefined;

  const isGhost = activeDragFile && selectedPaths.includes(activeDragFile.path) &&
                  selectedPaths.includes(file.path) && file.path !== activeDragFile.path;

  return (
    <div style={style}>
      <FileDetailsRowInner
        file={file}
        selected={selected}
        renaming={renaming}
        isGhost={!!isGhost}
        folderSize={folderSize}
        parentFolderSize={parentFolderSize}
        isSearchEngineReady={isSearchEngineReady}
        searchEngineStatus={searchEngineStatus}
        openSearchEngineInitModal={openSearchEngineInitModal}
        onRowClick={onRowClick}
        onRowDoubleClick={onRowDoubleClick}
        onRowContextMenu={onRowContextMenu}
        onDropFiles={onDropFiles}
        onRenameSubmit={onRenameSubmit}
        onRenameBlur={onRenameBlur}
      />
    </div>
  );
});

VirtualRow.displayName = 'VirtualRow';

// Inner row component (shared between virtual and non-virtual rendering)
interface FileDetailsRowInnerProps {
  file: FileStat;
  selected: boolean;
  renaming: boolean;
  isGhost?: boolean;
  folderSize?: FolderSizeResult;
  parentFolderSize?: FolderSizeResult | null;
  isSearchEngineReady: boolean;
  searchEngineStatus: FileSearchStatus;
  openSearchEngineInitModal: (isReinit?: boolean) => void;
  onRowClick: (path: string, e: React.MouseEvent) => void;
  onRowDoubleClick: (file: FileStat) => void;
  onRowContextMenu: (e: React.MouseEvent, path: string) => void;
  onDropFiles?: (files: { file: File, relativePath?: string }[], targetPath: string) => void;
  onRenameSubmit: (oldPath: string, e: React.KeyboardEvent<HTMLInputElement>) => void;
  onRenameBlur: (oldPath: string, value: string) => void;
}

const FileDetailsRowInner = memo<FileDetailsRowInnerProps>( ({
  file,
  selected,
  renaming,
  isGhost,
  folderSize,
  parentFolderSize,
  isSearchEngineReady,
  searchEngineStatus,
  openSearchEngineInitModal,
  onRowClick,
  onRowDoubleClick,
  onRowContextMenu,
  onDropFiles,
  onRenameSubmit,
  onRenameBlur,
}) => {
    const iconUrl = useFileIcon(file, 32);
    const typeLabel = getTypeLabel(file);
  
    // Generate tooltip with folder size info
    const tooltip = useMemo(() => {
      const lines = [
        `${t('name')}: ${file.name}`,
        `${t('type')}: ${typeLabel}`,
      ];
  
      let itemSize = 0;
      if (file.type === 'dir' && folderSize) {
        itemSize = folderSize.total_size;
        lines.push(`${t('size')}: ${formatSize(itemSize)}`);
      } else if (file.type === 'file') {
        itemSize = file.size;
        lines.push(`${t('size')}: ${formatSize(itemSize)}`);
      } else {
        lines.push(`${t('size')}: ${t('na')}`);
      }
  
      // Calculate percentage relative to current folder
      if (parentFolderSize && parentFolderSize.total_size > 0 && itemSize > 0) {
        const percent = (itemSize / parentFolderSize.total_size) * 100;
        lines.push(`${t('parentPercent')}: ${percent.toFixed(2)}%`);
      }
  
      if (file.type === 'dir' && folderSize) {
         lines.push(`${t('fileCount')}: ${folderSize.file_count.toLocaleString()}`);
      }
  
      lines.push(`${t('dateModified')}: ${formatDate(file.updatedAt)}`);
      return lines.join('\n');
    }, [file, typeLabel, folderSize, parentFolderSize]);
  
    // Drag & Drop Hooks
    const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
      id: file.path,
      data: { file }
    });
  
    const { setNodeRef: setDropRef, isOver } = useDroppable({
      id: file.path,
      data: { file },
      disabled: file.type === 'file'
    });
  
    const [isNativeOver, setIsNativeOver] = React.useState(false);
  
    const handleNativeDragOver = (e: React.DragEvent) => {
        if (file.type !== 'dir') return;
        if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault();
            e.stopPropagation();
            setIsNativeOver(true);
        }
    };
  
    const handleNativeDragLeave = (e: React.DragEvent) => {
        if (file.type !== 'dir') return;
        e.preventDefault();
        setIsNativeOver(false);
    };
  
    const handleNativeDrop = async (e: React.DragEvent) => {
        if (file.type !== 'dir') return;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            setIsNativeOver(false);
            
            if (onDropFiles) {
                const flatFiles = await processDropItems(e.dataTransfer.items);
                onDropFiles(flatFiles, file.path);
            }
        } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            setIsNativeOver(false);
            if (onDropFiles) {
                const files = Array.from(e.dataTransfer.files).map(f => ({ file: f, relativePath: f.name }));
                onDropFiles(files, file.path);
            }
        }
    };
  
    // Combine refs
    const setNodeRef = (node: HTMLElement | null) => {
      setDragRef(node);
      setDropRef(node);
    };
  
    const style = {
      height: ROW_HEIGHT,
      transform: CSS.Translate.toString(transform),
      opacity: isDragging ? 0 : (isGhost ? 0.4 : 1), // Hide active drag, dim others
    };
  
    return (
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        onClick={(e) => onRowClick(file.path, e)}
        onDoubleClick={() => onRowDoubleClick(file)}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRowContextMenu(e, file.path);
        }}
        onDragOver={handleNativeDragOver}
        onDragLeave={handleNativeDragLeave}
        onDrop={handleNativeDrop}
        data-path={file.path}
        title={tooltip}
        style={style}
        className={`file-item flex px-4 border-b border-transparent items-center group ${selected
            ? 'bg-blue-500/20 border-blue-500/10'
            : 'hover:bg-white/5 hover:border-white/5 odd:bg-white/[0.02]'
        } ${(isOver && !isDragging) || isNativeOver ? 'bg-blue-500/40 border-blue-500' : ''}`}
      >
      <div className="flex-1 flex items-center min-w-0 pr-4">
        <div className="mr-3 flex-shrink-0">
          {iconUrl ? (
            <img src={iconUrl} alt={file.name} className="w-8 h-8 object-contain" draggable={false} />
          ) : (
            file.type === 'dir'
              ? <Folder size={32} className="text-yellow-500" />
              : <FileIcon size={32} className="text-blue-400" />
          )}
        </div>
        {renaming ? (
          <RenameInput
            defaultValue={file.name}
            className="bg-white/10 border border-win-accent rounded px-1 outline-none w-full"
            onKeyDown={(e) => onRenameSubmit(file.path, e)}
            onBlur={(e) => onRenameBlur(file.path, e.currentTarget.value)}
          />
        ) : (
          <span className="truncate">{file.name}</span>
        )}
      </div>
      <div className="w-40 text-slate-400 truncate flex-shrink-0">{formatDate(file.updatedAt)}</div>
      <div className="w-24 text-slate-400 truncate flex-shrink-0">{typeLabel}</div>
      <div className="w-24 text-slate-400 text-right font-mono flex-shrink-0">
        {file.type === 'dir' ? (folderSize ? formatSize(folderSize.total_size) : '') : formatSize(file.size)}
      </div>
      <div className="w-32 flex-shrink-0 pl-2">
        {file.type === 'dir' && !isSearchEngineReady ? (
          // Search engine not ready - show status based on current state
          searchEngineStatus === FileSearchStatus.UNINITIALIZED ? (
            // Not initialized - show clickable "pending init" button
            <button
              onClick={(e) => {
                e.stopPropagation();
                openSearchEngineInitModal(false);
              }}
              className="text-xs text-yellow-500/80 hover:text-yellow-400 hover:underline cursor-pointer"
            >
              {t('percentPendingInit')}
            </button>
          ) : (
            // Initializing - show non-clickable text
            <span className="text-xs text-slate-400">{t('percentInitializing')}</span>
          )
        ) : (
          // Normal percentage display
          parentFolderSize && parentFolderSize.total_size > 0 && (() => {
            const itemSize = file.type === 'dir' && folderSize
              ? folderSize.total_size
              : file.type === 'file'
                ? file.size
                : 0;
            const percent = (itemSize / parentFolderSize.total_size) * 100;
            return itemSize > 0 ? <PercentBar percent={percent} size="sm" /> : null;
          })()
        )}
      </div>
    </div>
  );
});

FileDetailsRowInner.displayName = 'FileDetailsRowInner';

// Hook to measure container size
function useContainerSize(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateSize = () => {
      setSize({
        width: container.clientWidth,
        height: container.clientHeight,
      });
    };

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  return size;
}

const FileDetailsView: React.FC<FileDetailsViewProps> = ({ onContextMenu, folderSizes, currentFolderSize, fetchFolderSizes, activeDragFile, onDropFiles }) => {
  const { files, selectedPaths, setSelectedPaths, setCurrentPath, renamingPath, cancelRenaming, endRenaming, sortConfig, setSortConfig, openPreview, openSearchEngineInitModal } = useFileManagerStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const { width, height } = useContainerSize(containerRef);

  // Search engine status for "pending init" display
  const searchEngineStatus = useFileSearchStatusStore((s) => s.status);
  const isSearchEngineReady = searchEngineStatus === FileSearchStatus.READY;

  const sortedFiles = useMemo(() => {
    const isFolder = (file: FileStat) => file.type === 'dir' || file.type === 'drive';
    const getTypeSortValue = (file: FileStat) => {
      if (isFolder(file)) return 'folder';
      const ext = getFileExt(file.name);
      return ext || 'file';
    };
    const getSortValue = (file: FileStat) => {
      switch (sortConfig.key) {
        case 'name':
          return file.name.toLowerCase();
        case 'type':
          return getTypeSortValue(file);
        case 'size':
          if (file.type === 'dir' && folderSizes[file.path]) {
            return folderSizes[file.path].total_size;
          }
          return file.size;
        case 'updatedAt':
          return file.updatedAt;
        default:
          return file[sortConfig.key];
      }
    };
    return [...files].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      const aIsFolder = a.type === 'dir' || a.type === 'drive';
      const bIsFolder = b.type === 'dir' || b.type === 'drive';

      if (sortConfig.key === 'type') {
        if (aIsFolder !== bIsFolder) {
          return sortConfig.direction === 'desc'
            ? (aIsFolder ? -1 : 1)
            : (aIsFolder ? 1 : -1);
        }
        if (aValue < bValue) return -1 * direction;
        if (aValue > bValue) return 1 * direction;
        const nameCompare = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        return nameCompare * direction;
      }

      // Special handling for size sorting: folders and files are mixed if sorting by size
      if (sortConfig.key !== 'size') {
        if (a.type !== b.type) {
           if (a.type === 'dir') return -1;
           if (b.type === 'dir') return 1;
        }
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      
      // Fallback to name sort for equal values
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [files, sortConfig, folderSizes]);

  const handleSort = useCallback((key: 'name' | 'size' | 'updatedAt' | 'type') => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  }, [sortConfig, setSortConfig]);

  const SortIcon = ({ column }: { column: string }) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === 'asc' ? <ArrowUp size={12} className="ml-1" /> : <ArrowDown size={12} className="ml-1" />;
  };

  // Track last selected index for shift-click range selection
  const lastSelectedIndexRef = useRef<number>(-1);

  const handleRowClick = useCallback((path: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const currentIndex = sortedFiles.findIndex(f => f.path === path);

    if (e.shiftKey && lastSelectedIndexRef.current !== -1) {
      // Shift+click: select range
      const start = Math.min(lastSelectedIndexRef.current, currentIndex);
      const end = Math.max(lastSelectedIndexRef.current, currentIndex);
      const rangePaths = sortedFiles.slice(start, end + 1).map(f => f.path);

      if (e.ctrlKey) {
        // Ctrl+Shift: add range to existing selection
        const newSelection = [...new Set([...selectedPaths, ...rangePaths])];
        setSelectedPaths(newSelection);
      } else {
        // Shift only: replace selection with range
        setSelectedPaths(rangePaths);
      }
    } else if (e.ctrlKey) {
      // Ctrl+click: toggle single item
      if (selectedPaths.includes(path)) {
        setSelectedPaths(selectedPaths.filter(p => p !== path));
      } else {
        setSelectedPaths([...selectedPaths, path]);
      }
      lastSelectedIndexRef.current = currentIndex;
    } else {
      // Normal click: select single item
      setSelectedPaths([path]);
      lastSelectedIndexRef.current = currentIndex;
    }
  }, [sortedFiles, selectedPaths, setSelectedPaths]);

  const handleDoubleClick = useCallback((file: any) => {
    if (file.type === 'dir' || file.type === 'drive') {
      setCurrentPath(file.path);
    } else {
      openPreview(file);
    }
  }, [setCurrentPath, openPreview]);

  const handleRenameSubmit = useCallback((oldPath: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      endRenaming(oldPath, e.currentTarget.value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelRenaming();
    }
  }, [cancelRenaming, endRenaming]);

  const handleRenameBlur = useCallback((path: string, value: string) => {
    endRenaming(path, value);
  }, [endRenaming]);

  const handleContextMenuWrapper = useCallback((e: React.MouseEvent, path: string) => {
    onContextMenu(e, path);
  }, [onContextMenu]);

  // Memoize item data
  const itemData = useMemo(() => ({
    files: sortedFiles,
    selectedPaths,
    renamingPath,
    folderSizes,
    parentFolderSize: currentFolderSize,
    activeDragFile,
    isSearchEngineReady,
    searchEngineStatus,
    openSearchEngineInitModal,
    onDropFiles,
    onRowClick: handleRowClick,
    onRowDoubleClick: handleDoubleClick,
    onRowContextMenu: handleContextMenuWrapper,
    onRenameSubmit: handleRenameSubmit,
    onRenameBlur: handleRenameBlur,
  }), [sortedFiles, selectedPaths, renamingPath, folderSizes, currentFolderSize, activeDragFile, isSearchEngineReady, searchEngineStatus, openSearchEngineInitModal, onDropFiles, handleRowClick, handleDoubleClick, handleContextMenuWrapper, handleRenameSubmit, handleRenameBlur]);

  const useVirtualScroll = sortedFiles.length > VIRTUAL_THRESHOLD;
  const HEADER_HEIGHT = 40;
  const listHeight = Math.max(0, height - HEADER_HEIGHT);

  const handleItemsRendered = useCallback(({ visibleStartIndex, visibleStopIndex }: { visibleStartIndex: number; visibleStopIndex: number }) => {
    if (!useVirtualScroll) return;
    const visibleFolders = sortedFiles.slice(visibleStartIndex, visibleStopIndex + 1).filter(f => f.type === 'dir');
    if (visibleFolders.length > 0) {
      fetchFolderSizes(visibleFolders);
    }
  }, [sortedFiles, fetchFolderSizes, useVirtualScroll]);

  return (
    <div ref={containerRef} className="w-full h-full flex flex-col text-sm select-none overflow-hidden">
      {/* Header - Fixed */}
      <div className="flex px-4 py-2 border-b border-white/5 text-slate-400 font-medium bg-win-bg/90 backdrop-blur z-10 flex-shrink-0">
        <div onClick={() => handleSort('name')} className="flex-1 cursor-pointer hover:bg-white/5 px-2 rounded flex items-center">{t('name')} <SortIcon column="name" /></div>
        <div onClick={() => handleSort('updatedAt')} className="w-40 cursor-pointer hover:bg-white/5 px-2 rounded border-l border-white/5 flex items-center">{t('dateModified')} <SortIcon column="updatedAt" /></div>
        <div onClick={() => handleSort('type')} className="w-24 cursor-pointer hover:bg-white/5 px-2 rounded border-l border-white/5 flex items-center">{t('type')} <SortIcon column="type" /></div>
        <div onClick={() => handleSort('size')} className="w-24 cursor-pointer hover:bg-white/5 px-2 rounded border-l border-white/5 text-right flex items-center justify-end">{t('size')} <SortIcon column="size" /></div>
        <div className="w-32 px-2 border-l border-white/5 text-xs flex items-center">{t('parentPercent')}</div>
      </div>

      {/* Rows - Virtual or Regular */}
      {useVirtualScroll && width > 0 && listHeight > 0 ? (
        <FixedSizeList
          height={listHeight}
          itemCount={sortedFiles.length}
          itemSize={ROW_HEIGHT}
          width={width}
          itemData={itemData}
          overscanCount={5}
          onItemsRendered={handleItemsRendered}
          className="scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent flex-1"
        >
          {VirtualRow}
        </FixedSizeList>
      ) : (
        <div className="flex-1 overflow-auto">
          {sortedFiles.map((file) => {
            const folderSize = file.type === 'dir' ? folderSizes[file.path] : undefined;
            const isGhost = activeDragFile && selectedPaths.includes(activeDragFile.path) &&
                  selectedPaths.includes(file.path) && file.path !== activeDragFile.path;
            
            return (
              <FileDetailsRowInner
                key={file.path}
                file={file}
                selected={selectedPaths.includes(file.path)}
                renaming={renamingPath === file.path}
                isGhost={!!isGhost}
                folderSize={folderSize}
                parentFolderSize={currentFolderSize}
                isSearchEngineReady={isSearchEngineReady}
                searchEngineStatus={searchEngineStatus}
                openSearchEngineInitModal={openSearchEngineInitModal}
                onRowClick={handleRowClick}
                onRowDoubleClick={handleDoubleClick}
                onRowContextMenu={handleContextMenuWrapper}
                onDropFiles={onDropFiles}
                onRenameSubmit={handleRenameSubmit}
                onRenameBlur={handleRenameBlur}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FileDetailsView;